import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import styles from './contactDetail.module.scss';
import { Contact, EmailAssociation, DrmPage, ContactTabType } from 'v2/models';
import Avatar from 'v2/Avatar/Avatar';
import userProfile from '../../images/user-profile.png';
import { FormGroup } from 'react-bootstrap';
import { Tab, Tabs } from '@material-ui/core';
import { Link, useHistory } from 'react-router-dom';
import ContactDetailActions from './ContactDetailActions';
import EditContactModal, {
  EditionTypesText,
} from './ContactDetailTab/EditContactDetails/EditContactModal';
import SectionHeader from 'v2/common/DRM/SectionHeader/SectionHeader';
import HtmlRenderer from 'v2/common/HtmlRenderer/HtmlRenderer';
import useGetCustomTabs from 'v2/hooks/useGetCustomTabs';
import ContactTabContent from './ContactTabContent/ContactTabContent';
import { getContactNameToDisplay } from 'v2/utils/ContactUtils';
import useHistoryBackState from 'v2/hooks/useHistoryBackState';
import CopyEntityButton from 'v2/common/CopyEntityButton/CopyEntityButton';

const TABS: ContactTabType[] = [
  {
    eventKey: 'details',
    title: `details`,
  },
  {
    eventKey: 'associations',
    title: `associations`,
  },
  {
    eventKey: 'activity',
    title: `activity`,
  },
];

interface ContactDetailProps {
  contact: Contact;
  className?: string;
  refetch: () => void;
}

const ContactDetail = ({ contact, className, refetch }: ContactDetailProps) => {
  const [currentEditionType, setCurrentEditionType] = useState<{
    title: string;
    editionType: EditionTypesText;
  } | null>(null);

  const [selectedTab, setTabSelected] = useState<ContactTabType['eventKey']>(
    TABS[0].eventKey
  );

  const handleTabChange = (
    event: React.ChangeEvent<unknown>,
    newValue: ContactTabType['eventKey']
  ) => {
    setTabSelected(newValue as ContactTabType['eventKey']);
  };
  const [emailAssociations, setEmailAssociations] = useState(
    contact?.email_associations
  );

  const history = useHistory();
  const { counterToGoBack } = useHistoryBackState();

  function getEmails(): EmailAssociation[] {
    const emails: EmailAssociation[] =
      emailAssociations?.sort((email) => (email.is_primary ? -1 : 1)) || [];

    if (
      (emails.length > 0 && emails[0].is_primary) ||
      !contact.primary_associations?.email
    ) {
      return emails;
    }
    emails.unshift(contact.primary_associations.email);
    return emails;
  }

  const emails = useMemo(getEmails, [emailAssociations]);

  const handleEditEmail = (emails: EmailAssociation[]) => {
    setEmailAssociations(emails);
    refetch();
  };

  const { data: customTabs } = useGetCustomTabs(DrmPage.CONTACT);

  const handleEditClose = () => {
    setCurrentEditionType(null);
    refetch();
  };

  return (
    <>
      <div
        className={classnames(
          'container-xl',
          'h-100',
          styles.contactDetailsContainer,
          className
        )}
      >
        <div
          className={classnames(
            'pr-5 d-flex fix-bg-images pl-4',
            styles.leftContent
          )}
        >
          <FormGroup>
            <div
              className="d-flex flex-row"
              data-cy="contact-details:contact-header-card"
            >
              <Avatar
                url={contact.avatar_url || userProfile}
                className={styles.avatar}
                initials={contact.first_name?.[0]}
              />
              <div
                className={classnames(
                  'ml-3 d-flex flex-column',
                  styles.nameContainer
                )}
              >
                <label className={styles.name}>
                  {getContactNameToDisplay(contact)} <CopyEntityButton />
                </label>

                {contact.primary_associations.data_vendor ? (
                  <label>
                    {contact.title}
                    {contact.title ? ' at ' : ''}{' '}
                    <Link
                      data-cy="contact:link-to-primary-datavendor"
                      data-testid={`primary_datavendor`}
                      key={contact.primary_associations.data_vendor.id}
                      to={{
                        pathname: `/data-provider-details/${contact.primary_associations?.data_vendor?.id}/detail`,
                        state: {
                          counterToGoBack: -1,
                          backLinkTitle: getContactNameToDisplay(contact),
                        },
                      }}
                    >
                      {contact.primary_associations.data_vendor.name}
                    </Link>
                  </label>
                ) : (
                  <label data-testid={`contact_title`}>
                    {contact.title}{' '}
                    {contact.company_name
                      ? `${contact.title ? 'at ' : ''}${contact.company_name}`
                      : ''}
                  </label>
                )}
              </div>
              <div
                className={classnames(
                  'mt-2 d-flex flex-column',
                  styles.actions
                )}
              >
                <ContactDetailActions
                  contact={contact}
                  onDeleted={() => history.go(counterToGoBack)}
                  onChange={() => {
                    setCurrentEditionType({
                      editionType: EditionTypesText.profile,
                      title: 'Edit Profile',
                    });
                  }}
                />
              </div>
            </div>
            <div className="d-flex flex-column">
              <div
                className="d-flex flex-row"
                data-cy="contact-details:email-card"
              >
                <div className="d-flex flex-column">
                  <label className={styles.subtitles}>Email</label>
                </div>
                <div className="d-flex flex-column ml-auto">
                  <ContactDetailActions.EditEmail
                    onEdit={handleEditEmail}
                    contact={{ ...contact, email_associations: emails }}
                  />
                </div>
              </div>
              {emails.map((email) => (
                <div
                  key={email.email}
                  className="d-flex"
                  data-testid={`email_${email.id}`}
                >
                  <HtmlRenderer className="mr-3" source={email.email} />{' '}
                  {email.is_primary && (
                    <div className={styles.primaryBox}>Primary</div>
                  )}
                </div>
              ))}
              <SectionHeader
                title="Description"
                isEditable={contact.is_editable}
                className={styles.description}
                onClick={() =>
                  setCurrentEditionType({
                    editionType: EditionTypesText.description,
                    title: 'Edit Description',
                  })
                }
              />
              {contact.description && (
                <HtmlRenderer source={contact.description} />
              )}
            </div>
          </FormGroup>
        </div>

        <div className={classnames(styles.tabs_container)}>
          <Tabs
            className={classnames(styles.scrolling_tabs)}
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            indicatorColor="primary"
            textColor="primary"
            data-cy="contact:tabs"
          >
            {TABS.map((item) => (
              <Tab
                key={item.eventKey}
                className={classnames(styles.tabs)}
                value={item.eventKey}
                label={`${item.title}`}
              ></Tab>
            ))}
            {customTabs?.results.map((tab) => (
              <Tab
                key={tab.id}
                className={classnames(styles.tabs)}
                value={tab.name}
                label={`${tab.name}`}
              />
            ))}
          </Tabs>
          <div>
            <ContactTabContent
              selectedTab={selectedTab}
              customTabs={customTabs}
              TABS={TABS}
              contact={contact}
              refetch={refetch}
            />
          </div>
        </div>
      </div>
      {currentEditionType && (
        <EditContactModal
          show={currentEditionType.editionType !== null}
          contact={contact}
          editionType={currentEditionType.editionType}
          onClose={handleEditClose}
          title={currentEditionType.title}
        />
      )}
    </>
  );
};

export default ContactDetail;
